import React, {
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
} from 'react';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import presetNewsletter from 'grapesjs-preset-newsletter';
import { uploadImageToS3 } from 'src/contexts/utils/uploadImageToS3';
import { AWS_S3 } from 'src/constants/crm/aws';
import { HTMLEditorRef } from './types';
import { makeStyles, Theme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) => ({
    '@global': {
        '.gjs-one-bg': {
            color: theme.palette.primary.contrastText + ' !important',
            boxShadow: 'none !important',
            backgroundColor: theme.palette.primary.main + ' !important',
        },
        '.gjs-pn-active': {
            backgroundColor: theme.palette.secondary.main + ' !important',
            color: theme.palette.secondary.contrastText + ' !important',
        },
        '.gjs-mdl-header': {
            backgroundColor: theme.palette.background.default + ' !important',
            color: theme.palette.text.primary + ' !important',
        },
        '.gjs-mdl-content': {
            backgroundColor: theme.palette.background.default + ' !important',
        },
        '.gjs-two-color': {
            backgroundColor: theme.palette.primary.main + ' !important',
            color: theme.palette.primary.contrastText + ' !important',
            boxShadow: 'none !important',
        },
        '.gjs-btn-prim': {
            borderRadius: '4px !important',
            padding: '4px 24px !important',
            height: '56px !important',
            fontSize: '14px !important',
            backgroundColor: theme.palette.primary.main + ' !important',
            color: theme.palette.primary.contrastText + ' !important',
        },
        '.gjs-editor': {
            border: `1px solid ${theme.palette.divider} !important`,
            backgroundColor: theme.palette.background.default + ' !important',
        },
    },
}));

export const HTMLEditor = forwardRef<
    HTMLEditorRef,
    { onChange?: () => void; height?: string }
>(({ onChange, height = '84vh' }, ref) => {
    useStyles();
    const editorRef = useRef<HTMLDivElement>(null);
    const editorInstance = useRef<any>(null);

    useEffect(() => {
        if (!editorRef.current) return;

        editorInstance.current = grapesjs.init({
            container: editorRef.current,
            fromElement: false,
            height,
            plugins: [presetNewsletter],
            storageManager: false,
            assetManager: {
                upload: false,
                uploadFile: async (event: any) => {
                    const input = event.target as HTMLInputElement;

                    const files = event.dataTransfer
                        ? event.dataTransfer.files
                        : input.files;

                    const results = [];

                    const fileArray = Array.from(files || []) as File[];

                    for (const file of fileArray) {
                        const { key } = await uploadImageToS3(file);
                        const fileName = file.name.split('/').pop();

                        results.push({
                            src: AWS_S3 + key,
                            key: fileName,
                        });
                    }

                    results.forEach((img) => {
                        editorInstance.current.AssetManager.add(img);
                    });
                },
            },
            styleManager: {
                sectors: [
                    {
                        name: 'General',
                        open: true,
                        buildProps: ['float', 'display', 'position'],
                    },
                    {
                        name: 'Typography',
                        open: true,
                        buildProps: [
                            'font-family',
                            'font-size',
                            'color',
                            'line-height',
                            'text-align',
                        ],
                    },
                    {
                        name: 'Decorations',
                        open: true,
                        buildProps: [
                            'background-color',
                            'border-radius',
                            'padding',
                        ],
                    },
                ],
            },
        });

        editorInstance.current.on('update', () => {
            if (onChange) onChange();
        });

        return () => editorInstance.current?.destroy();
    }, []);

    useImperativeHandle(ref, () => ({
        getHtml: () => editorInstance.current?.getHtml() || '',
        getCss: () => editorInstance.current?.getCss() || '',
        hasContent: () => {
            const editor = editorInstance.current;
            const components = editor.getWrapper().components();
            return components.length > 0;
        },
        getFullHtml: () => {
            const html = editorInstance.current?.getHtml() || '';
            const css = editorInstance.current?.getCss() || '';

            return `
                <html>
                <head>
                    <meta charset="UTF-8" />
                    <style>${css}</style>
                </head>
                ${html}
                </html>
            `;
        },
    }));

    return <div ref={editorRef} />;
});
